import { LitElement, html } from 'lit';
import '../quote/quote-header.js';
import '../quote/quote-class-section.js';
import '../quote/quote-financial-summary.js';
import '../quote/po-requirement-choice.js';
import '../icons/success-icon.js';
import '../icons/checkmark-icon.js';
import './po-reference-card.js';
import './document-download-card.js';

class QuoteConfirmed extends LitElement {
    static properties = {
        quoteReference: { type: String },
        quoteData: { type: Object },
    };

    createRenderRoot() {
        return this; // Light DOM for Tailwind compatibility
    }

    constructor() {
        super();
        this.quoteData = null;
    }

    get customerName() {
        return this.quoteData?.custName || 'N/A';
    }

    get startDate() {
        return this.quoteData?.startDate || 'N/A';
    }

    get serviceClasses() {
        return this.quoteData?.class || [];
    }

    get isChargeAccount() {
        return this.quoteData?.isMUF === 'N';
    }

    // Customer contact details
    get customerContactName() {
        return this.quoteData?.contactName || 'N/A';
    }

    get customerContactEmail() {
        return this.quoteData?.contactEmail || '';
    }

    // Sales rep (RM) contact details
    get rmContactName() {
        return this.quoteData?.rmContact || 'REACH Team';
    }

    get rmContactEmail() {
        return this.quoteData?.rmContactEmail || 'info@reach.nz';
    }

    // Formatted contact strings for display
    get customerContactFormatted() {
        if (!this.customerContactEmail) return this.customerContactName;
        return html`${this.customerContactName} (<a
                href="mailto:${this.customerContactEmail}"
                class="link"
                >${this.customerContactEmail}</a
            >)`;
    }

    get rmContactFormatted() {
        if (!this.rmContactEmail) return this.rmContactName;
        return html`${this.rmContactName} (<a href="mailto:${this.rmContactEmail}" class="link"
                >${this.rmContactEmail}</a
            >)`;
    }

    _handlePoChoice(e) {
        this.dispatchEvent(
            new CustomEvent('submit-po-required', {
                detail: { custRefRequired: e.detail.custRefRequired },
                bubbles: true,
                composed: true,
            })
        );
    }

    _renderPoSection() {
        const custRefRequired = this.quoteData?.custRefRequired;

        // Not yet decided -- show the Y/N choice cards only
        if (custRefRequired !== 'Y' && custRefRequired !== 'N') {
            return html`
                <po-requirement-choice
                    choiceOnly
                    @po-choice-changed="${this._handlePoChoice}"
                ></po-requirement-choice>
            `;
        }

        // PO required -- show PO reference card (input or read-only)
        if (custRefRequired === 'Y') {
            return html`
                <po-reference-card
                    quoteReference="${this.quoteReference}"
                    custRef="${this.quoteData?.custRef || ''}"
                ></po-reference-card>
            `;
        }

        // PO not required -- show nothing
        return '';
    }

    render() {
        if (!this.quoteData) {
            return html`<div></div>`;
        }

        return html`
            <div class="space-y-6 py-6">
                <!-- Confirmation Alert -->
                <div class="alert alert-row alert-success">
                    <success-icon class="shrink-0"></success-icon>
                    <div class="text-left">
                        <h3 class="font-bold">
                            ${this.isChargeAccount ? 'Booking Confirmed!' : 'Quote Confirmed!'}
                        </h3>
                        <p class="text-sm">
                            ${this.isChargeAccount
                                ? html`
                                      Thank you, <strong>${this.customerName}</strong>! Your booking
                                      is now confirmed. As a charge account customer, an invoice
                                      will be sent to you on campaign start date.
                                  `
                                : html`
                                      Thank you, <strong>${this.customerName}</strong>! Your quote
                                      has been accepted and payment received.
                                  `}
                        </p>
                    </div>
                </div>

                <div class="mb-8">
                    <h2 class="text-3xl font-bold mb-2">Confirmation Details</h2>
                    <div class="space-y-2">
                        <p class="text-gray-600">
                            Your quote reference is <strong>${this.quoteReference}</strong> and
                            contract number is
                            <strong>${this.quoteData.contractNo || 'N/A'}</strong>.
                        </p>
                        <p class="text-gray-600">
                            <strong>Customer Contact:</strong> ${this.customerContactFormatted}
                        </p>
                        <p class="text-gray-600">
                            <strong>Your Account Manager:</strong> ${this.rmContactFormatted}
                        </p>
                    </div>
                </div>

                <!-- PO Section (CA customers only) -->
                ${this.isChargeAccount ? this._renderPoSection() : ''}

                <!-- Download Documents -->
                <document-download-card
                    quoteReference="${this.quoteReference}"
                    quoteId="${this.quoteReference}"
                    custRef="${this.quoteData?.custRef || ''}"
                    cardTitle="Booking Document"
                    cardDescription="Download your Booking Confirmation Document for quote ${this
                        .quoteReference} for your reference."
                    buttonText="Download Booking Document"
                ></document-download-card>

                ${this.isChargeAccount
                    ? ''
                    : html`
                          <document-download-card
                              quoteReference="${this.quoteReference}"
                              quoteId="${this.quoteReference}"
                              custRef="${this.quoteData?.custRef || ''}"
                              cardTitle="Invoice"
                              cardDescription="Download your invoice for quote ${this
                                  .quoteReference} for your reference."
                              buttonText="Download Invoice"
                              documentType="invoice"
                          ></document-download-card>
                      `}

                <!-- Quote Summary -->
                <div class="mb-4">
                    <h3 class="text-xl font-bold mb-4">Quote Summary</h3>
                    <quote-header .quoteData="${this.quoteData}"></quote-header>
                </div>

                <!-- What Happens Next -->
                <div class="card bg-base-200">
                    <div class="card-body">
                        <h3 class="card-title text-xl mb-4">What Happens Next?</h3>

                        <div class="space-y-4">
                            <div class="flex gap-4">
                                <div
                                    class="flex-shrink-0 w-10 h-10 rounded-full bg-peppermint/20 flex items-center justify-center"
                                >
                                    <span class="font-bold text-peppermint">1</span>
                                </div>
                                <div>
                                    <h4 class="font-bold">Confirmation Email Sent</h4>
                                    <p class="text-sm text-gray-600">
                                        A confirmation email have been sent to
                                        ${this.customerContactFormatted} and to your Account Manager
                                        ${this.rmContactFormatted} with your campaign details.
                                        Another email will be sent once your campaign is completed.
                                    </p>
                                </div>
                            </div>

                            <div class="flex gap-4">
                                <div
                                    class="flex-shrink-0 w-10 h-10 rounded-full bg-peppermint/20 flex items-center justify-center"
                                >
                                    <span class="font-bold text-peppermint">2</span>
                                </div>
                                <div>
                                    <h4 class="font-bold">Campaign Preparation</h4>
                                    <p class="text-sm text-gray-600">
                                        Our team will begin preparing your campaign for the
                                        scheduled start date.
                                    </p>
                                </div>
                            </div>

                            <div class="flex gap-4">
                                <div
                                    class="flex-shrink-0 w-10 h-10 rounded-full bg-peppermint/20 flex items-center justify-center"
                                >
                                    <span class="font-bold text-peppermint">3</span>
                                </div>
                                <div>
                                    <h4 class="font-bold">Stay in Touch</h4>
                                    <p class="text-sm text-gray-600">
                                        Your Account Manager ${this.rmContactFormatted} will contact
                                        you if any additional information is needed. Feel free to
                                        reach out anytime.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Service Classes -->
                <div class="space-y-6 mt-8">
                    ${this.serviceClasses.map(
                        (classData) => html`
                            <quote-class-section
                                .classData="${classData}"
                                .sputnikQuoteId="${this.quoteData?.sputnikQuoteId}"
                            ></quote-class-section>
                        `
                    )}
                </div>

                <!-- Financial Summary -->
                <quote-financial-summary .quoteData="${this.quoteData}"></quote-financial-summary>

                <!-- Support Contact -->
                <div class="card bg-base-200 mt-8">
                    <div class="card-body">
                        <h3 class="card-title">Need Help?</h3>
                        <p class="text-sm">
                            For questions about your confirmed quote, please contact your account
                            manager ${this.rmContactFormatted}, or reach out to our support team.
                        </p>
                        <div class="card-actions justify-end mt-4">
                            <a
                                href="mailto:${this.rmContactEmail}?subject=Quote%20${this
                                    .quoteReference}%20-%20Confirmed"
                                class="btn btn-primary"
                            >
                                Email Account Manager
                            </a>
                            <a
                                href="mailto:info@reach.nz?subject=Quote%20${this
                                    .quoteReference}%20-%20Confirmed"
                                class="btn btn-outline"
                            >
                                Contact Support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('quote-confirmed', QuoteConfirmed);
